import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import {
  CreateEmployerDto,
  DocumentType,
  EmployerDto,
  EmployerSignupStatus,
  TaxIdType,
} from '@zorro/clients';
import {
  getQueryValue,
  logger,
  responseErrorToString,
} from '@zorro/common-utils';
import { tryFormatDateISO, tryParseDateISO } from '@zorro/shared/formatters';
import { YesNo } from '@zorro/types';
import {
  callEndpoint,
  createEmployerDocumentUrl,
  createEmployerLogoUrl,
  showErrorNotification,
  showWarningNotification,
  useDynamicRouter,
  useEmployerFeatureFlag,
  useForm,
  useImpersonation,
  useMonolithQuery,
  useRoles,
} from '@zorro/web/ui-utils';
import { Dispatch, SetStateAction, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';

import {
  CompanyDetailsFormFields,
  getCompanyDetailsSchemaObject,
} from './CompanyDetailsUtils';

type UseCompanyDetailsForm = (props: {
  isDon: boolean;
  isFinalizationMode: boolean;
  isSignedAgreementVisible: boolean;
  employerId?: string;
  explicitEmployerStatus?: EmployerSignupStatus;
}) => {
  isSignedAgreementRequired: boolean;
  isLynxPaymentsEnabled: boolean;
  companyDetailsForm: UseFormReturn<CompanyDetailsFormFields>;
  hasLogoBeenDeleted: boolean;
  setHasLogoBeenDeleted: Dispatch<SetStateAction<boolean>>;
  onSubmit: () => Promise<{ employerId: string } | null>;
};

export const useCompanyDetailsForm: UseCompanyDetailsForm = ({
  employerId,
  isDon,
  isFinalizationMode,
  isSignedAgreementVisible,
  explicitEmployerStatus,
}) => {
  const { isOn: isLynxPaymentsEnabled } = useEmployerFeatureFlag(
    'lynx_payments_enabled',
    employerId
  );
  const isSignedAgreementRequired =
    isSignedAgreementVisible && isFinalizationMode && !isDon;

  const { router } = useDynamicRouter({});
  const isDemoWhenAddingNewEmployer =
    getQueryValue(router.query.isDemo) === 'true' || false;

  const { user } = useImpersonation();
  const { isAgent } = useRoles();

  const queryClient = useQueryClient();

  const { data: employer } = useMonolithQuery({
    method: 'employersControllerFindOne',
    params: employerId && [employerId],
  });

  const { data: documents } = useMonolithQuery({
    method: 'employerDocumentControllerGetDocuments',
    params: employerId && [employerId],
  });

  const { data: beneficialOwners } = useMonolithQuery({
    method: 'employersControllerGetBeneficialOwners',
    params: employerId && isLynxPaymentsEnabled && [employerId],
  });

  const [hasLogoBeenDeleted, setHasLogoBeenDeleted] = useState(false);

  const previousSignedAgreement = documents?.find(
    ({ isLegalAgreement }) => isLegalAgreement
  );

  const companyDetailsForm = useForm<CompanyDetailsFormFields>({
    mode: 'all',
    resolver: yupResolver(
      getCompanyDetailsSchemaObject(
        isFinalizationMode,
        isSignedAgreementRequired,
        isLynxPaymentsEnabled
      )
    ),
    defaultValues: {
      name: employer?.name || '',
      legalName: employer?.legalName || '',
      isLegalNameSameAsCompanyName:
        employer?.name && employer?.legalName
          ? employer.name === employer.legalName
          : true,
      websiteUrl: employer?.websiteUrl || undefined,
      stateOfIncorporation: employer?.stateOfIncorporation || undefined,
      yearsInBusiness: employer?.yearsInBusiness ?? undefined,
      address: employer?.address || '',
      mailingAddress: employer?.mailingAddress || '',
      isMailingAddressSameAsHQAddress:
        employer?.address && employer?.mailingAddress
          ? employer.address === employer.mailingAddress
          : true,
      businessEntityType: employer?.businessEntityType,
      taxIdType: employer?.taxIdType ?? TaxIdType.EIN,
      taxId: employer?.taxId || '',
      logo: employer?.logoUrl || undefined,
      agreementSignedDate: tryParseDateISO(
        employer?.agreementSignedDate
      )?.toDate(),
      naicsCode: employer?.naicsCode || undefined,
      signedAgreement: previousSignedAgreement || undefined,
      hasBeneficialOwners:
        isLynxPaymentsEnabled && beneficialOwners?.length
          ? beneficialOwners[0].isOfficer
            ? YesNo.NO
            : YesNo.YES
          : undefined,
      beneficialOwners: isLynxPaymentsEnabled
        ? beneficialOwners?.length
          ? beneficialOwners.map((owner) => ({
              firstName: owner.firstName || '',
              lastName: owner.lastName || '',
              phone: owner.phone || '',
              email: owner.email || '',
              address: owner.address || '',
              dateOfBirth: owner.dateOfBirth
                ? tryParseDateISO(owner.dateOfBirth)?.toDate()
                : undefined,
              ssn: owner.ssn || '',
              isAdmin: false,
              isDuplicateMode: false,
            }))
          : []
        : [],
    },
  });

  return {
    companyDetailsForm,
    hasLogoBeenDeleted,
    setHasLogoBeenDeleted,
    isSignedAgreementRequired,
    isLynxPaymentsEnabled,
    onSubmit: async () => {
      const data = companyDetailsForm.getValues();
      const { signedAgreement } = data;

      let logo: string | undefined = undefined;
      if (data.logo instanceof File) {
        const { logoUrl } = await createEmployerLogoUrl(data.logo);

        logo = logoUrl;
      } else if (typeof data.logo === 'string') {
        logo = data.logo;
      }

      // Draft is only needed when creating a new employer
      const employerStatus =
        explicitEmployerStatus ??
        employer?.signupStatus ??
        EmployerSignupStatus.DRAFT;

      const body: CreateEmployerDto = {
        name: data.name,
        legalName: data.isLegalNameSameAsCompanyName
          ? data.name
          : data.legalName,
        websiteUrl: data.websiteUrl || null,
        stateOfIncorporation: data.stateOfIncorporation ?? null,
        yearsInBusiness: data.yearsInBusiness ?? null,
        address: data.address,
        mailingAddress: data.isMailingAddressSameAsHQAddress
          ? data.address
          : data.mailingAddress,
        taxId: data.taxId,
        taxIdType: data.taxIdType,
        logoUrl: logo,
        businessEntityType: data.businessEntityType || undefined,
        signupStatus: employerStatus,
        enrollmentTeamIds: employer?.enrollmentTeamIds || [],
        zorroPartnerId: isAgent
          ? employer?.zorroPartnerId || user?.tenantId || null
          : employer?.zorroPartnerId || null,
        producerId: employer?.producerId || null,
        producerAgentId: employer?.producerAgentId || null,
        isDemo: employer?.isDemo || isDemoWhenAddingNewEmployer,
        isPayrollSyncOn: employer?.isPayrollSyncOn ?? false,
        isPartnersHubOn: employer?.isPartnersHubOn ?? true,
        isEmploymentSyncOn: employer?.isEmploymentSyncOn ?? false,
        ccEmailList: employer?.ccEmailList || [],
        isMonthlySEPDeadlineEndOfMonth:
          employer?.isMonthlySEPDeadlineEndOfMonth || false,
        monthlySEPDeadline: employer?.monthlySEPDeadline || 20,
        agreementSignedDate:
          tryFormatDateISO(data?.agreementSignedDate) ||
          employer?.agreementSignedDate,
        naicsCode: data.naicsCode,
        beneficialOwners: isLynxPaymentsEnabled
          ? data.beneficialOwners
              ?.filter((beneficialOwner) =>
                Object.values(beneficialOwner).some(Boolean)
              )
              .map((beneficialOwner, index) => ({
                firstName: beneficialOwner.firstName || undefined,
                lastName: beneficialOwner.lastName || undefined,
                email: beneficialOwner.email || undefined,
                phone: beneficialOwner.phone || undefined,
                address: beneficialOwner.address || undefined,
                dateOfBirth:
                  tryFormatDateISO(beneficialOwner.dateOfBirth) || undefined,
                ssn: beneficialOwner.ssn || undefined,
                isOfficer: data.hasBeneficialOwners === YesNo.NO,
                isPrimary: index === 0,
              }))
          : undefined,
      };

      let newEmployer: EmployerDto;
      try {
        newEmployer = employerId
          ? (
              await callEndpoint({
                method: 'employersControllerUpdate',
                params: [employerId, body],
              })
            ).employer
          : await callEndpoint({
              method: 'employersControllerCreate',
              params: [body],
            });
      } catch (error) {
        logger.error(error);
        showErrorNotification({ message: responseErrorToString(error) });

        return null;
      }

      try {
        if (signedAgreement instanceof File) {
          const { documentUrl, fileName } =
            await createEmployerDocumentUrl(signedAgreement);

          if (previousSignedAgreement) {
            await callEndpoint({
              method: 'employerDocumentControllerDeleteDocument',
              params: [newEmployer.id, previousSignedAgreement?.id],
            });
          }

          await callEndpoint({
            method: 'employerDocumentControllerPostDocument',
            params: [
              newEmployer.id,
              {
                type: DocumentType.ADMIN,
                isLegalAgreement: true,
                documentUrl,
                fileName,
              },
            ],
          });
        }

        await queryClient.invalidateQueries();
      } catch (error) {
        logger.error(error);
        showWarningNotification({ message: responseErrorToString(error) });
      }

      return { employerId: newEmployer.id };
    },
  };
};
